// Task writes that show before the server answers (WORK-PERF; api/optimistic.ts). Every write
// patches the cached card everywhere it shows (task-cache.ts), then writes the server's copy over
// it; a refusal puts the cache back and the query client's error toast says why.
//
// One task's writes go to the server in the order they were made (a per-task queue), and each
// reads the task's version when it is sent, so a second quick edit carries the version the first
// one produced (`If-Match`) instead of failing on a stale one. While a later write of the same
// task is still queued, an answer only advances the version: the later optimistic copy stays on
// screen until its own answer.
//
// What settles in the background afterwards is narrow: the task's history and the inbox (what a
// write changes besides the card), never every Work list.
import { newIdempotencyKey } from "@companyos/sdk";
import { toast } from "@companyos/ui";
import { type QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import { reachRefusalToast } from "../agents/ask-access";
import type { CompanyAccess } from "../company";
import { taskRef } from "./model";
import {
  cachedTask,
  cachedTasks,
  type LocalTask,
  patchTask,
  queued,
  removeTask,
  taskKeys,
  toCard,
  updateTask,
  writesInFlight,
} from "./task-cache";
import { type Board, isDoneStatus, type TaskDetail } from "./view-engine";
import { invalidateWork, workKeys } from "./work-queries";

/** Writes of the task still queued or in flight (beyond the one answering now). */
const later = writesInFlight;

/** The task's version now (the freshest cached copy). */
const versionOf = (qc: QueryClient, co: string, id: string, fallback?: number) =>
  cachedTask(qc, co, id)?.version ?? fallback;

/** The server's copy of a task: the detail as is, the card everywhere else. */
export function reconcileTask(
  qc: QueryClient,
  co: string,
  d: TaskDetail,
  o: { insert?: boolean } = {},
) {
  if (later(d.id) > 0) {
    updateTask(qc, co, d.id, (t) => ({ ...t, version: Math.max(t.version, d.version) }));
    return;
  }
  const extra = patchTask(qc, co, toCard(d), o);
  if (qc.getQueryData(workKeys.task(co, d.id))) qc.setQueryData(workKeys.task(co, d.id), d);
  for (const queryKey of extra) void qc.invalidateQueries({ queryKey });
}

const boardOf = (qc: QueryClient, co: string, id: string) =>
  qc.getQueryData<Board>(workKeys.board(co, id));

/** A task with `status`, completed or reopened as the board's statuses say. */
function withStatus(qc: QueryClient, co: string, t: LocalTask, status: string): LocalTask {
  const statuses = boardOf(qc, co, t.board_id)?.status_set ?? [];
  const done = isDoneStatus(statuses, status);
  return {
    ...t,
    status,
    completed_at: done ? (t.completed_at ?? new Date().toISOString()) : null,
  };
}

const around = (co: string, id: string): QueryKey[] => [
  workKeys.history(co, id),
  ["company", co, "work", "inbox"],
];

export type NewTask = {
  board_id: string;
  title: string;
  status?: string;
  priority?: number;
  due_at?: string | null;
  parent_task_id?: string | null;
  start_at?: string | null;
  body_markdown?: string;
  assignee_seat_ids?: string[];
  fields?: Record<string, unknown>;
  /** B4-ITEM-TEMPLATES: a board's item template fills what this leaves out. */
  template_id?: string;
};

/** The card a new task will most likely be: the board's first status, the next number. */
function provisional(qc: QueryClient, co: string, v: NewTask, clientId: string): LocalTask {
  const now = new Date().toISOString();
  let max = 0;
  for (const t of cachedTasks(qc, co)) if (t.number > max) max = t.number;
  return {
    tag_ids: [],
    company_id: co,
    id: clientId,
    board_id: v.board_id,
    number: max + 1,
    title: v.title,
    status: v.status ?? boardOf(qc, co, v.board_id)?.status_set[0]?.key ?? "todo",
    priority: v.priority ?? 0,
    parent_task_id: v.parent_task_id ?? null,
    page_id: null,
    thread_id: null,
    due_at: v.due_at ?? null,
    start_at: null,
    estimate_minutes: null,
    created_by_seat_id: null,
    completed_at: null,
    origin: "manual" as LocalTask["origin"],
    origin_ref: null,
    client_id: clientId,
    restricted: false,
    version: 1,
    actor_kind: null,
    actor_membership_id: null,
    created_at: now,
    updated_at: now,
    assignee_seat_ids: v.assignee_seat_ids ?? [],
    fields: v.fields ?? {},
    checklist: { done: 0, total: 0 },
    blocked: false,
    depends_on_task_ids: [],
    local: { state: "saving" },
  };
}

// The hook ------------------------------------------------------------------------------------

/** Every write a task row, card, drawer or bulk bar makes, each on screen at once. */
export function useTaskWrites(api: Api, company: CompanyAccess) {
  const co = company.id;
  const qc = useQueryClient();
  const keys = () => taskKeys(co);

  const status = useOptimisticMutation({
    keys,
    apply: (v: { id: string; status: string }) =>
      updateTask(qc, co, v.id, (t) => withStatus(qc, co, t, v.status)),
    mutationFn: (v) =>
      queued(v.id, () =>
        api.client.tasks.setStatus({ params: { co, id: v.id }, body: { status: v.status } }),
      ),
    reconcile: (d) => reconcileTask(qc, co, d),
    settle: (v) => around(co, v.id),
  });

  const assign = useOptimisticMutation({
    keys,
    apply: (v: { id: string; seat: string; on: boolean }) =>
      updateTask(qc, co, v.id, (t) => ({
        ...t,
        assignee_seat_ids: v.on
          ? [...new Set([...t.assignee_seat_ids, v.seat])]
          : t.assignee_seat_ids.filter((s) => s !== v.seat),
      })),
    mutationFn: (v) =>
      queued(v.id, async () => {
        if (v.on)
          return api.client.tasks.assign({
            params: { co, id: v.id },
            body: { seat_ids: [v.seat] },
          });
        await api.client.tasks.unassign({ params: { co, id: v.id, seat_id: v.seat } });
        return null;
      }),
    reconcile: (d) => {
      if (d) reconcileTask(qc, co, d);
    },
    // SHARED-DES: a shared DE outside whose grants the caller is: the refusal with "Ask for
    // access" (features/agents/ask-access.tsx), not the plain error toast.
    meta: { quietCodes: ["not_in_reach"] },
    onFailure: (e, _v, rollback) => {
      rollback();
      // Any other problem: the query client's toast already spoke.
      reachRefusalToast(api, co, e);
    },
    // My tasks gains or loses the row only by the server's rule (the caller's seats).
    settle: (v) => [...around(co, v.id), workKeys.myTasks(co)],
  });

  const update = useOptimisticMutation({
    keys,
    apply: (v: {
      id: string;
      body: {
        title?: string;
        priority?: number;
        due_at?: string | null;
        start_at?: string | null;
        /** Parity P1: the drawer's Estimate. */
        estimate_minutes?: number | null;
      };
    }) => updateTask(qc, co, v.id, (t) => ({ ...t, ...v.body })),
    mutationFn: (v) =>
      queued(v.id, () => {
        // The version the previous write of this task produced (the row on screen has it).
        return api.client.tasks.update({
          params: { co, id: v.id },
          ifMatch: versionOf(qc, co, v.id) ?? 0,
          body: v.body,
        });
      }),
    reconcile: (d) => reconcileTask(qc, co, d),
    settle: (v) => around(co, v.id),
  });

  const setField = useOptimisticMutation({
    keys,
    apply: (v: { id: string; field: string; value: unknown }) =>
      updateTask(qc, co, v.id, (t) => ({ ...t, fields: { ...t.fields, [v.field]: v.value } })),
    mutationFn: (v) =>
      queued(v.id, () =>
        api.client.tasks.setField({
          params: { co, id: v.id, field_id: v.field },
          body: { value: v.value },
        }),
      ),
    // Formulas and rollups over the field are the server's to compute.
    settle: (v) => [...around(co, v.id), workKeys.task(co, v.id)],
  });

  const move = useOptimisticMutation({
    keys,
    apply: (v: { id: string; boardId: string; status?: string }) =>
      updateTask(qc, co, v.id, (t) => {
        const moved = { ...t, board_id: v.boardId };
        return v.status ? withStatus(qc, co, moved, v.status) : moved;
      }),
    mutationFn: (v) =>
      queued(v.id, () =>
        api.client.tasks.move({
          params: { co, id: v.id },
          body: { board_id: v.boardId, ...(v.status ? { status: v.status } : {}) },
        }),
      ),
    reconcile: (d) => reconcileTask(qc, co, d, { insert: true }),
    settle: (v) => around(co, v.id),
  });

  const remove = useOptimisticMutation({
    keys,
    apply: (v: { id: string }) => removeTask(qc, co, v.id),
    mutationFn: (v) => queued(v.id, () => api.client.tasks.delete({ params: { co, id: v.id } })),
    settle: () => [workKeys.myTasks(co), ["company", co, "work", "inbox"]],
  });

  const create = useOptimisticMutation({
    keys,
    apply: (v: NewTask & { clientId: string }) =>
      patchTask(qc, co, provisional(qc, co, v, v.clientId), { insert: true }),
    mutationFn: ({ clientId, ...v }) =>
      api.client.tasks.create({
        params: { co },
        body: {
          board_id: v.board_id,
          client_id: clientId,
          title: v.title,
          ...(v.status ? { status: v.status } : {}),
          ...(v.priority !== undefined ? { priority: v.priority } : {}),
          ...(v.due_at ? { due_at: v.due_at } : {}),
          ...(v.parent_task_id ? { parent_task_id: v.parent_task_id } : {}),
          ...(v.start_at ? { start_at: v.start_at } : {}),
          ...(v.body_markdown ? { body_markdown: v.body_markdown } : {}),
          // with a template, an empty list is said: none (left out, the template's would apply)
          ...(v.assignee_seat_ids?.length || (v.template_id && v.assignee_seat_ids)
            ? { assignee_seat_ids: v.assignee_seat_ids }
            : {}),
          ...(v.fields ? { fields: v.fields } : {}),
          ...(v.template_id ? { template_id: v.template_id } : {}),
        },
      }),
    reconcile: (d) => reconcileTask(qc, co, d, { insert: true }),
    onSuccess: (t) =>
      toast.add({ title: `${taskRef(t)} created`, description: t.title, type: "success" }),
    // The sidebar's counts, My tasks (an assignee given) and Home's board summary (its done /
    // total, else it reads 0/0 for up to the 30 s stale time) follow the server.
    settle: () => [workKeys.myTasks(co), ["company", co, "work", "space"], workKeys.summaries(co)],
  });

  type BulkAction =
    | { kind: "status"; status: string }
    | { kind: "assign"; seat_ids: string[] }
    | { kind: "move"; board_id: string }
    | { kind: "delete" }
    // WOS-BUNDLE: tag or untag the selection.
    | { kind: "tag"; tag_id: string; op: "add" | "remove" };
  const bulk = useOptimisticMutation({
    keys,
    apply: (v: { ids: string[]; action: BulkAction }) => {
      for (const id of v.ids) {
        const a = v.action;
        if (a.kind === "delete") removeTask(qc, co, id);
        else
          updateTask(qc, co, id, (t) =>
            a.kind === "status"
              ? withStatus(qc, co, t, a.status)
              : a.kind === "move"
                ? { ...t, board_id: a.board_id }
                : a.kind === "tag"
                  ? {
                      ...t,
                      tag_ids:
                        a.op === "add"
                          ? [...new Set([...(t.tag_ids ?? []), a.tag_id])]
                          : (t.tag_ids ?? []).filter((x) => x !== a.tag_id),
                    }
                  : {
                      ...t,
                      assignee_seat_ids: [...new Set([...t.assignee_seat_ids, ...a.seat_ids])],
                    },
          );
      }
    },
    mutationFn: (v) =>
      api.client.tasks.bulk({ params: { co }, body: { task_ids: v.ids, action: v.action } }),
    // A bulk answer has no cards (and some tasks may have refused): every Work list catches up
    // behind the optimistic copy.
    onSuccess: () => void invalidateWork(qc, co),
  });

  return {
    /** Parity P1: `onError` sees a refusal too (the drawer highlights `required_missing`). */
    setStatus: (id: string, s: string, o?: { onError?: (e: unknown) => void }) =>
      status.mutate({ id, status: s }, o?.onError ? { onError: o.onError } : undefined),
    toggleAssignee: (id: string, seat: string, on: boolean) => assign.mutate({ id, seat, on }),
    update: (id: string, body: Parameters<typeof update.mutate>[0]["body"]) =>
      update.mutate({ id, body }),
    setField: (id: string, field: string, value: unknown) => setField.mutate({ id, field, value }),
    move: (
      id: string,
      boardId: string,
      o: { status?: string; onSuccess?: (t: TaskDetail) => void } = {},
    ) =>
      move.mutate(
        { id, boardId, ...(o.status ? { status: o.status } : {}) },
        o.onSuccess ? { onSuccess: o.onSuccess } : undefined,
      ),
    remove: (id: string, o?: { onSuccess?: () => void }) => remove.mutate({ id }, o),
    /** A new task (inline, `c`, a dialog, a subtask); the row shows before the server answers. */
    create: (v: NewTask, o?: { clientId?: string; onSuccess?: (t: TaskDetail) => void }) =>
      create.mutate(
        { ...v, clientId: o?.clientId ?? newIdempotencyKey() },
        o?.onSuccess ? { onSuccess: o.onSuccess } : undefined,
      ),
    createPending: create.isPending,
    bulk,
    invalidate: () => invalidateWork(qc, co),
  };
}
